import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';

import {
  changePassword,
  getCompanyList,
  getSwitchCompany,
  getSwitchableCompanyList,
  postLogin,
  sendEmail,
  sendEmailForPassword,
  switchCompany,
  verifyEmailCode,
  postLoginComplete,
  sendLoginEmailCode,
} from '@entities/auth/api/auth';
import useAuthStore from '@entities/auth/model/useAuthStore';
import { putDevice } from '@entities/device/api/device';
import { setLastCompanyCode } from '@shared/lib/cookie';
import { getDeviceInfo } from '@shared/utils/deviceInfo';
import { getFcmToken, isMobileApp } from '@shared/utils/fcmToken';

/**
 * 현재 OS 타입을 반환합니다 (모바일 환경에서만 유효).
 * @returns {'ios' | 'android' | undefined} OS 타입 또는 undefined
 */
function getOsType() {
  if (typeof navigator !== 'undefined') {
    const userAgent = navigator.userAgent || navigator.vendor || window.opera;
    if (/android/i.test(userAgent)) {
      return 'android';
    }
    if (/iPad|iPhone|iPod/.test(userAgent) && !window.MSStream) {
      return 'ios';
    }
  }
  return undefined;
}

/**
 * 회사 목록 조회용 React Query 커스텀 훅
 * useCompanyListQuery
 * - getCompanyList API를 호출하여 회사 리스트 데이터를 비동기로 가져옴
 * - React Query의 캐싱, 로딩, 에러 상태를 자동 관리함
 * - queryKey: 캐싱 및 refetch 시 구분용 키 ('companyList')
 * - queryFn: 실제 데이터 호출 함수 (getCompanyList)
 * - select: API 응답 중 필요한 데이터(companyList)만 추출
 * @returns {UseQueryResult<Array>} 회사 목록 데이터 및 쿼리 상태 정보
 */
function useCompanyListQuery() {
  return useQuery({
    queryKey: ['companyList'],
    queryFn: getCompanyList,
    select(data) {
      return data?.resultData?.companyList ?? [];
    },
    // 회사 목록은 거의 변경되지 않으므로 1시간 캐싱
    staleTime: 1000 * 60 * 60,
  });
}
const LOGIN_ERROR_TYPE = {
  ACCOUNT_LOCKED: 'ACCOUNT_LOCKED',
  INVALID_PASSWORD: 'INVALID_PASSWORD',
  LOGIN_FAILED: 'LOGIN_FAILED',
  NETWORK_ERROR: 'NETWORK_ERROR',
};

/**
 * useLoginMutation
 * @param {Object} [options] - 추가 옵션 (onSuccess, onError 등)
 * @returns {UseMutationResult} 로그인 요청 및 상태를 관리하는 React Query Mutation 객체
 */
function useLoginMutation(options = {}) {
  const setAuth = useAuthStore((state) => state.setAuth);

  return useMutation({
    mutationFn: async ({ userId, passwd, companyCode, rememberMe }) => {
      const isMobile = isMobileApp();
      const deviceInfo = isMobile ? getDeviceInfo() : null;
      // 모바일: Flutter가 주입한 디바이스 정보 우선, 없으면 userAgent 기반 getOsType() 사용
      const osType = isMobile ? (deviceInfo?.osType ?? getOsType()) : undefined;
      // 백엔드는 deviceId를 device_model로 저장. Flutter에서 deviceModel 또는 deviceId 전달
      const deviceId = isMobile
        ? (deviceInfo?.deviceModel ?? deviceInfo?.deviceId ?? undefined)
        : undefined;
      const pushToken = isMobile ? getFcmToken() : undefined;

      return postLogin({
        userId,
        passwd,
        companyCode,
        rememberMe,
        deviceId,
        pushToken,
        osType,
      });
    },
    onSuccess: (data) => {
      const code = data?.result_code;

      if (code !== 0) {
        let friendlyMsg = data?.error_msg || '로그인에 실패했습니다.';

        // 상태 분리
        const errorType =
          code === 103
            ? LOGIN_ERROR_TYPE.ACCOUNT_LOCKED
            : code === 101
              ? LOGIN_ERROR_TYPE.INVALID_PASSWORD
              : LOGIN_ERROR_TYPE.LOGIN_FAILED;

        if (options.onError) {
          options.onError({
            friendlyMsg,
            errorType,
          });
        }
        return;
      }

      //  로그인 성공 처리
      const { resultData } = data;
      const { accessToken, enumber, companyCode, tokenType, expiresIn, ...userData } = resultData;

      // Zustand store에 모든 인증 정보 저장
      // - user/enumber/companyCode 등은 localStorage에 유지 (accessToken 제외)
      // - accessToken/token은 sessionStorage에 유지(브라우저/탭 종료 시 제거)
      setAuth(
        {
          ...userData,
          expiresIn,
        },
        accessToken,
        enumber,
        companyCode,
      );
      setLastCompanyCode(companyCode);
      if (options.onSuccess) options.onSuccess(data);
    },
    onError: (error) => {
      const message = error?.response?.data?.error_msg || '서버와 연결할 수 없습니다.';

      if (options.onError) {
        options.onError({
          friendlyMsg: message,
          errorType: LOGIN_ERROR_TYPE.NETWORK_ERROR,
        });
      }
    },
  });
}

/**
 * useGetSwitchCompanyMutaion
 * - 회사 컨텍스트 변경(Switch Company) 기능을 수행하는 Mutation 훅
 * - 백엔드는 GET 메서드이지만, 실제 기능은 상태 변경이므로 Mutation으로 처리함
 *
 * @param {object} options - onSuccess, onError 등 React Query Mutation 옵션
 * @returns {UseMutationResult}
 */
function useGetSwitchCompanyMutaion(options = {}) {
  return useMutation({
    mutationFn: (companyCode) => getSwitchCompany(companyCode),
    ...options,
  });
}
/**
 * 로그인한 사용자가 로그인할 회사 변경시 변경 가능한 회사 리스트
 * @param {string} userId : 아이디
 * @param {string} passwd : 비밀번호
 */
function useGetSwitchableCompanyListQuery(userId, passwd, options = {}) {
  return useQuery({
    queryKey: ['switchableCompanyList', userId],
    queryFn: () => getSwitchableCompanyList(userId, passwd),
    enabled: Boolean(userId && passwd),
    ...options,
  });
}

/**
 * 이메일 인증번호 발송 Mutation 훅
 * - name, email을 인자로 받아 서버에 인증번호 발송 요청
 */
function useSendEmailMutation(options = {}) {
  return useMutation({
    mutationFn: ({ name, email }) => sendEmail(name, email),
    ...options,
  });
}
/**
 * 이메일 인증번호 검증 Mutation 훅
 * @param {Object} options React Query 옵션(onSuccess, onError 등)
 */
function useVerifyEmailCodeMutation(options = {}) {
  return useMutation({
    /**
     * @param {Object} payload
     * @param {string} payload.name
     * @param {string} payload.email
     * @param {string} payload.code
     * @param {string} payload.requestId
     * @param {string} payload.purpose
     */
    mutationFn: (payload) => verifyEmailCode(payload),
    ...options,
  });
}
/**
 * 이메일 인증번호 발송 Mutation 훅
 * - name, email,id을 인자로 받아 서버에 인증번호 발송 요청
 */
function useSendEmailForPasswordMutation(options = {}) {
  return useMutation({
    mutationFn: ({ name, email, id, companyCode }) =>
      sendEmailForPassword(name, email, id, companyCode),
    ...options,
  });
}

/**
 * 비밀번호 찾기 후 비밀번호 변경 Mutation
 */
function useResetPasswordMutation(options = {}) {
  return useMutation({
    mutationFn: ({ email, resetToken, changePw }) =>
      changePassword({ email, resetToken, changePw }),
    ...options,
  });
}

/**
 * useSwitchCompanyMutation
 * - 계정전환(회사 변경) 기능을 수행하는 Mutation 훅
 * - 성공 시 AuthStore 업데이트, React Query 캐시 초기화, 홈 화면으로 이동
 *
 * @param {object} options - onSuccess, onError 등 React Query Mutation 옵션
 * @returns {UseMutationResult}
 */
function useSwitchCompanyMutation(options = {}) {
  const setAuth = useAuthStore((state) => state.setAuth);
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  return useMutation({
    mutationFn: (companyCode) => switchCompany(companyCode),
    onSuccess: (data) => {
      const code = data?.result_code;

      if (code !== 0) {
        const friendlyMsg = data?.error_msg || '계정 전환에 실패했습니다.';
        if (options.onError) {
          options.onError({ friendlyMsg });
        }
        return;
      }

      // 계정전환 성공 처리
      const { resultData } = data;
      const { accessToken, enumber, companyCode: newCompanyCode, ...userData } = resultData;

      // Zustand store에 새로운 인증 정보 저장
      setAuth(userData, accessToken, enumber, newCompanyCode);
      setLastCompanyCode(newCompanyCode);

      // React Query 캐시 전체 초기화 (이전 회사 데이터 제거)
      queryClient.clear();

      // 홈 화면으로 이동
      navigate('/', { replace: true });

      if (options.onSuccess) options.onSuccess(data);
    },
    onError: (error) => {
      const message = error?.response?.data?.error_msg || '서버와 연결할 수 없습니다.';

      if (options.onError) {
        options.onError({ friendlyMsg: message });
      }
    },
  });
}

/**
 * 이메일 인증 완료 후 로그인 마무리 Mutation (Redis 기반)
 * - userId, companyCode를 서버로 전달
 */
function useLoginCompleteMutation(options = {}) {
  const setAuth = useAuthStore((state) => state.setAuth);

  return useMutation({
    // payload를 받도록 수정
    mutationFn: ({ userId, companyCode, requestId, emailCode }) =>
      postLoginComplete({ userId, companyCode, requestId, emailCode }),

    onSuccess: (data) => {
      const code = data?.result_code;

      if (code !== 0) {
        const friendlyMsg = data?.error_msg || '로그인에 실패했습니다.';
        options.onError?.({ friendlyMsg });
        return;
      }

      const { resultData } = data;
      const { accessToken, enumber, companyCode, expiresIn, loginStatus, tokenType, ...userData } =
        resultData;

      setAuth(
        {
          ...userData,
          expiresIn,
          loginStatus,
          tokenType,
        },
        accessToken,
        enumber,
        companyCode,
      );
      setLastCompanyCode(companyCode);

      options.onSuccess?.(data);
    },

    onError: (error) => {
      const message = error?.response?.data?.error_msg || '서버와 연결할 수 없습니다.';
      options.onError?.({ friendlyMsg: message });
    },
  });
}

/**
 * [로그인 전용]
 * 사외망 로그인 이메일 인증번호 발송 Mutation
 *
 * @param {Object} options - React Query mutation options
 * @returns {UseMutationResult}
 */
function useSendLoginEmailCodeMutation(options = {}) {
  return useMutation({
    /**
     * @param {Object} payload
     * @param {string} payload.userId
     * @param {number} payload.companyCode
     */
    mutationFn: ({ userId, companyCode, email }) =>
      sendLoginEmailCode({ userId, companyCode, email }),

    ...options,
  });
}

export {
  useCompanyListQuery,
  useLoginMutation,
  useGetSwitchCompanyMutaion,
  useGetSwitchableCompanyListQuery,
  useSendEmailMutation,
  useVerifyEmailCodeMutation,
  useSendEmailForPasswordMutation,
  useResetPasswordMutation,
  useSwitchCompanyMutation,
  useLoginCompleteMutation,
  useSendLoginEmailCodeMutation,
};
